/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN INVESTORS API
 * Role: Admin
 * Scope: Tenant-based investor management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */
const ADMIN_INVESTORS_ENDPOINT = "/admin/Investors/investors.php";

/* =============================================================
   GET – Fetch Investors (Admin View)
   Supports: pagination, search, status filtering
============================================================= */
export const getAdminInvestors = async (
  { page = 1, limit = 10, search = "", status = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_INVESTORS_ENDPOINT,
      null,
      "Admin – Fetch Investors",
      {
        params: { page, limit, search, status },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch investors",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error("Admin Fetch Investors Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Investor
============================================================= */
export const getAdminInvestorById = async (id, options = {}) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid investor ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_INVESTORS_ENDPOINT}?id=${id}`,
      null,
      "Admin – Fetch Investor By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch investor",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error("Admin Fetch Investor By ID Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create Investor
============================================================= */
export const createAdminInvestor = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_INVESTORS_ENDPOINT,
      payload,
      "Admin – Create Investor",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to create investor",
      };
    }

    return {
      success: true,
      data: res.data,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Create Investor Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Investor
============================================================= */
export const updateAdminInvestor = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid investor ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_INVESTORS_ENDPOINT}?id=${id}`,
      payload,
      "Admin – Update Investor",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to update investor",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message: res.message || "Investor updated successfully",
    };

  } catch (error) {
    console.error("Admin Update Investor Error:", error);

    return {
      success: false,
      message: error?.message || "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Remove Investor
============================================================= */
export const deleteAdminInvestor = async (id, options = {}) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid investor ID",
      };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_INVESTORS_ENDPOINT}?id=${id}`,
      null,
      "Admin – Delete Investor",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to delete investor",
      };
    }

    return {
      success: true,
      message: res.message,
    };
  } catch (error) {
    console.error("Admin Delete Investor Error:", error);

    return {
      success: false,
      message: error.message || "Unexpected error occurred",
    };
  }
};